import React from "react";
import { BrowserRouter, Routes, Route, Navigate, useNavigate } from "react-router-dom";

import { ToastProvider } from "./components/common/ToastProvider";
import ErrorBoundary from "./components/manager/shared/ErrorBoundary";
import { getRole, getToken } from "./services/auth";
import { useI18n } from "./i18n/I18nProvider";

/* ===== PAGE IMPORTS ===== */
import Login from "./pages/Login";
import ReceptionAutoRouter from "./components/Reception/ReceptionAutoRouter";
import DoctorAutoRouter from "./components/doctor/DoctorAutoRouter";
import PharmacyAutoRouter from "./components/pharmacy/PharmacyAutoRouter";
import ManagerAutoRouter from "./components/manager/ManagerAutoRouter";
import TreatmentsModule from "./components/treatments/TreatmentsModule";
import FollowupAutoRouter from "./components/followups/FollowupAutoRouter";
import BillingClinical from "./pages/BillingClinical";
import FastBilling from "./pages/FastBilling";
import AdminHome from "./pages/AdminHome";
import Print from "./pages/Print";
import ToothChartPreview from "./pages/ToothChartPreview";
import DoctorChartTest from "./pages/DoctorChartTest";
import PatientPortal from "./pages/PatientPortal";
import WaitingRoom from "./pages/WaitingRoom";
import ThemeToggle from "./components/common/ThemeToggle";
import PatientWorkflow from "./pages/PatientWorkflow";
import Ledgers from "./pages/Ledgers";

function roleFromPath(pathname: string): string | null {
  if (pathname.startsWith("/reception")) return "reception";
  if (pathname.startsWith("/manager")) return "manager";
  if (pathname.startsWith("/doctor")) return "doctor";
  if (pathname.startsWith("/admin")) return "admin";
  if (pathname.startsWith("/pharmacy")) return "pharmacy";
  if (pathname.startsWith("/careflow")) return "clinic";
  if (pathname.startsWith("/fast-billing")) return "clinic";
  if (pathname.startsWith("/patient")) return "reception";
  if (pathname.startsWith("/ledgers")) return "manager";
  if (pathname.startsWith("/treatments")) return "manager"; // Allow manager/doctor/admin
  return null;
}

function homeForRole(role?: string | null): string {
  switch ((role || "").toLowerCase()) {
    case "admin": return "/admin";
    case "doctor": return "/doctor";
    case "manager": return "/manager";
    case "pharmacy": return "/pharmacy";
    case "reception": return "/reception";
    case "clinic": return "/reception";
    default: return "/reception";
  }
}

function RequireAuth({
  children,
  allowedRoles,
}: {
  children: React.ReactElement;
  allowedRoles?: string[];
}) {
  const token = getToken();
  const location = window.location.pathname;
  if (!token) {
    const role = roleFromPath(location);
    const redirect = encodeURIComponent(location);
    const roleParam = role ? `role=${encodeURIComponent(role)}&` : "";
    return <Navigate to={`/login?${roleParam}redirect=${redirect}`} replace />;
  }
  const role = getRole();
  if (allowedRoles && role && !allowedRoles.includes(role)) {
    return <Navigate to={homeForRole(role)} replace />;
  }
  return children;
}

export default function App() {
  return (
    <ErrorBoundary>
      <BrowserRouter>
        <ToastProvider>
          <AppFrame />
        </ToastProvider>
      </BrowserRouter>
    </ErrorBoundary>
  );
}



// ... inside AppFrame component ...

function DesktopOnlyNotice({ moduleName }: { moduleName: string }) {
  const nav = useNavigate();
  const { t } = useI18n();
  return (
    <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", height: "80vh", gap: 16, textAlign: "center", padding: "0 24px" }}>
      <div style={{ fontSize: "3rem" }}>🖥️</div>
      <h2 style={{ margin: 0, color: "#1e293b" }}>{moduleName}</h2>
      <p style={{ color: "#64748b", maxWidth: 320 }}>
        {t("app_desktop_only_description")}
      </p>
      <button
        type="button"
        onClick={() => nav(-1)}
        style={{ padding: "10px 24px", background: "#0ea5e9", color: "#fff", border: "none", borderRadius: 8, cursor: "pointer", fontWeight: 600 }}
      >
        {t("app_go_back")}
      </button>
    </div>
  );
}

function AppFrame() {
  const isTouchViewport =
    typeof window !== "undefined" && window.matchMedia("(max-width: 1199px)").matches;
  const isMobileViewport =
    typeof window !== "undefined" && window.matchMedia("(max-width: 768px)").matches;
  const enableDebugRoutes = process.env.REACT_APP_ENABLE_DEBUG_ROUTES === "true";

  return (
    <>
      <Routes>
        {/* Default Redirect */}
        <Route path="/" element={<Navigate to={homeForRole(getRole())} replace />} />
        <Route
          path="/login"
          element={getToken() ? <Navigate to={homeForRole(getRole())} replace /> : <Login />}
        />
        <Route path="/portal" element={<PatientPortal />} />
        <Route path="/waitroom" element={<WaitingRoom />} />

        {/* --- CORE MODULES --- */}
        <Route
          path="/careflow"
          element={
            <RequireAuth allowedRoles={["clinic", "admin", "doctor", "manager", "pharmacy", "reception"]}>
              <FastBilling />
            </RequireAuth>
          }
        />
        <Route
          path="/reception"
          element={
            <RequireAuth allowedRoles={["reception", "clinic", "admin"]}>
              <ReceptionAutoRouter />
            </RequireAuth>
          }
        />
        <Route
          path="/patient/:patientId"
          element={
            <RequireAuth allowedRoles={["reception", "clinic", "admin", "doctor", "manager", "pharmacy"]}>
              <PatientWorkflow />
            </RequireAuth>
          }
        />
        <Route
          path="/doctor"
          element={
            <RequireAuth allowedRoles={["doctor", "clinic", "admin"]}>
              <DoctorAutoRouter />
            </RequireAuth>
          }
        />

        {/* Pharmacy + Manager: mobile view on small screens, desktop otherwise */}
        <Route
          path="/pharmacy"
          element={
            <RequireAuth allowedRoles={["pharmacy", "clinic", "admin", "reception"]}>
              <PharmacyAutoRouter />
            </RequireAuth>
          }
        />
        <Route
          path="/manager"
          element={
            <RequireAuth allowedRoles={["manager", "clinic", "admin"]}>
              <ManagerAutoRouter />
            </RequireAuth>
          }
        />
        <Route
          path="/ledgers"
          element={
            <RequireAuth allowedRoles={["manager", "clinic", "admin"]}>
              <Ledgers />
            </RequireAuth>
          }
        />

        {/* NEW TREATMENTS MODULE */}
        <Route
          path="/treatments"
          element={
            <RequireAuth allowedRoles={["manager", "clinic", "admin", "doctor"]}>
              {isMobileViewport ? <DesktopOnlyNotice moduleName="Treatments" /> : <TreatmentsModule />}
            </RequireAuth>
          }
        />

        {/* NEW FOLLOWUP MODULE */}
        <Route
          path="/followups"
          element={
            <RequireAuth allowedRoles={["manager", "clinic", "admin", "doctor"]}>
              {isMobileViewport ? <DesktopOnlyNotice moduleName="Followups" /> : <FollowupAutoRouter />}
            </RequireAuth>
          }
        />

        <Route
          path="/billing/clinical"
          element={
            <RequireAuth allowedRoles={["manager", "clinic", "admin"]}>
              {isTouchViewport ? <FastBilling /> : <BillingClinical />}
            </RequireAuth>
          }
        />
        <Route
          path="/fast-billing"
          element={
            <Navigate to="/careflow" replace />
          }
        />
        <Route
          path="/admin"
          element={
            <RequireAuth allowedRoles={["admin"]}>
              <AdminHome />
            </RequireAuth>
          }
        />

        {/* --- UTILITIES --- */}
        {enableDebugRoutes && (
          <Route
            path="/test-chart"
            element={
              <RequireAuth allowedRoles={["doctor", "admin"]}>
                <ToothChartPreview />
              </RequireAuth>
            }
          />
        )}
        {enableDebugRoutes && (
          <Route
            path="/chart-debug"
            element={
              <RequireAuth allowedRoles={["doctor", "admin"]}>
                <DoctorChartTest />
              </RequireAuth>
            }
          />
        )}
        <Route
          path="/print"
          element={
            <RequireAuth allowedRoles={["admin", "reception", "manager", "doctor", "pharmacy"]}>
              <Print />
            </RequireAuth>
          }
        />

        {/* Fallback */}
        <Route path="*" element={<Navigate to="/reception" replace />} />
      </Routes>
    </>
  );
}
